import { Order } from '@seen/core/models/orders/Order'
import { getReferenceString } from '@seen/core/references'
import { type JSX, useEffect, useRef } from 'react'
import { OrderCreatedToast } from '#src/components/order/OrderCreatedToast.tsx'
import { OrderSplitToast } from '#src/components/order/OrderSplitToast.tsx'
import { useInFlightLabSubmissionsStore } from '#src/components/order/useInFlightLabSubmissionsStore.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { toast } from '#src/utils/toast.ts'

/**
 * Mounts at the app root and polls every lab order this session dispatched
 * to Health Gorilla until the subscription handler reaches a terminal state.
 * Decouples polling from the LabOrderForm component so a navigation away
 * mid-flight still resolves toast UX. Keeps polling session-scoped on purpose
 * — refreshes drop the session set, and the order's pulsing status icon
 * (driven by the server-side `submissionState` tag) provides the persistent
 * UX without recreating toast state.
 *
 * @returns A fragment containing one polling component per in-flight order id.
 */
export function LabSubmissionWatcher(): JSX.Element {
  const dispatchedOrderIds = useInFlightLabSubmissionsStore(
    (s) => s.dispatchedOrderIds
  )

  return (
    <>
      {Array.from(dispatchedOrderIds).map((orderId) => (
        <LabSubmissionPoll key={orderId} orderId={orderId} />
      ))}
    </>
  )
}

/**
 * Detects whether a terminal order was split into siblings by the Health
 * Gorilla split bot. The original order is marked `entered-in-error`; the
 * new sibling orders point back at it via `replaces`, which is what we
 * reverse-search to discover them.
 *
 * @param order - The order being polled.
 * @returns True when the order was replaced by split siblings.
 */
function wasSplit(order: Order): boolean {
  return order.status === 'entered-in-error'
}

/**
 * Polls a single in-flight order until terminal, then surfaces the
 * appropriate toast (success, error, or split) and removes the id from the
 * session store.
 *
 * @param props - Component props.
 * @param props.orderId - Id of the order to poll.
 * @returns Always null; this component is side-effect-only.
 */
function LabSubmissionPoll({ orderId }: { orderId: string }): null {
  const medplum = useMedplum()
  const removeInFlight = useInFlightLabSubmissionsStore((s) => s.remove)
  const handledRef = useRef(false)

  const { data: order } = useRead(Order, orderId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data
        if (!data) {
          return 3000
        }
        return data.isSubmitting ? 3000 : false
      },
    },
  })

  // The dispatch mutation only adds an order id to the in-flight store
  // after the POST returns, by which point the server has committed
  // `markRequested`. So the first poll here is guaranteed to observe
  // `isSubmitting === true` (covers both `requested` and `dispatched`
  // server states) or a terminal state from a prior pass — no "saw
  // submitting first" gate needed.
  const isTerminal = !!order && !order.isSubmitting
  const hasSubmissionError = !!order?.hasSubmissionError
  const submissionError = order?.submissionError

  useEffect(() => {
    if (!isTerminal || handledRef.current || !order) {
      return
    }
    handledRef.current = true

    const loadingToastId = `lab-order-submission-${orderId}`

    const finish = async (): Promise<void> => {
      toast.dismiss(loadingToastId)

      if (hasSubmissionError) {
        toast.error(
          `Failed to submit lab order: ${submissionError ?? 'Unknown error'}`
        )
      } else if (wasSplit(order)) {
        const siblings = await medplum.searchResourceClass(Order, {
          replaces: getReferenceString(order.reference),
        })
        if (siblings.length > 0) {
          toast.success(
            <OrderSplitToast
              originalOrder={order}
              resultingOrders={siblings}
            />,
            { id: `lab-order-split-${orderId}` }
          )
        } else {
          toast.success(<OrderCreatedToast order={order} />, {
            id: `lab-order-submission-success-${orderId}`,
          })
        }
      } else {
        toast.success(<OrderCreatedToast order={order} />, {
          id: `lab-order-submission-success-${orderId}`,
        })
      }

      removeInFlight(orderId)
    }

    void finish()
  }, [
    isTerminal,
    hasSubmissionError,
    submissionError,
    orderId,
    order,
    medplum,
    removeInFlight,
  ])

  return null
}
